import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';

import { getRegionById } from '../../../services/regions.services';
import { RegionStatus } from '../../../types/fair.types';
import { usersService } from '../../../services/users.service';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getActiveFairsInRegion,
  addJudgeToFair,
  removeJudgeFromFair,
} from '../../../services/fairs.services';

// Interface for judge with check-in status
interface Judge {
  judgeId: string;
  judgeCheckedIn: boolean;
}

//Interfaces for region and fair
interface Region {
  id: string;
  name: string;
  location: string;
  regionDirectorId: string;
  state: string;
  status: RegionStatus;
  maxAmountOfFairs: number;
  schools: string[];
}

interface Fair {
  id: string;
  name: string;
  addressLine: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  startDate: Date;
  judges: Judge[]; // Updated from judgeIds to judges array
}

const FairRegistration: React.FC = () => {
  // Region Detail Hooks
  const [region, setRegion] = useState<Region | null>(null);
  const [fairs, setFairs] = useState<Fair[] | null>([]);
  // Dialog Hooks and States
  const [directorInfo, setDirectorInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Registration Dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'register' | 'unregister'>(
    'register'
  );
  const [selectedFair, setSelectedFair] = useState<Fair | null>(null);
  const [registrationStatus, setRegistrationStatus] = useState('');
  //Auth and User Hooks
  const { authStatus } = useAuth();

  const fetchUserRegion = useCallback(async () => {
    setLoading(true);
    try {
      const userId = authStatus.user?.uid;

      if (!userId) {
        setError('User information not available.');
        setLoading(false);
        return;
      }

      const userRegionId = await usersService.getUserRegion(userId);
      const userRegion = await getRegionById(userRegionId);

      if (!userRegion) {
        setError('You are not associated with any region.');
        setLoading(false);
        return;
      }

      setRegion({
        id: userRegion.id,
        name: userRegion.name,
        location: userRegion.location,
        regionDirectorId: userRegion.regionDirectorId,
        state: userRegion.state,
        status: userRegion.status,
        maxAmountOfFairs: userRegion.maxAmountOfFairs,
        schools: userRegion.schools || [],
      });
    } catch (error) {
      console.error('Error fetching regions:', error);
      setError('Failed to load region information');
    } finally {
      setLoading(false);
    }
  }, [authStatus.user?.uid]);

  useEffect(() => {
    fetchUserRegion();
  }, [fetchUserRegion]);

  const fetchDirectorInfo = useCallback(async () => {
    if (!region || !region.regionDirectorId) return;
    try {
      const userData = await usersService.getUserAccount(
        region.regionDirectorId
      );
      setDirectorInfo(userData);
    } catch (error) {
      console.error('Error fetching director details:', error);
    }
  }, [region]);

  const fetchFairsInRegion = useCallback(async () => {
    if (!region) return;
    try {
      const rawData = await getActiveFairsInRegion(
        authStatus.metadata?.regionId ?? ''
      );
      if (!rawData) {
        setError('No fairs found in this region.');
        return;
      }
      //Map the fairs to the Fair interface and ensure startDate is a Date object
      const data: Fair[] = rawData.map((fair: any) => ({
        id: fair.id,
        name: fair.name,
        addressLine: fair.addressLine,
        addressCity: fair.addressCity,
        addressState: fair.addressState,
        addressZip: fair.addressZip,
        startDate: fair.startTime,
        judges: fair.judges || [], // Updated to use judges array
      }));
      setFairs(data);
    } catch (error) {
      console.error('Error fetching fairs in region:', error);
    }
  }, [region, authStatus.metadata?.regionId]);

  useEffect(() => {
    if (region) {
      fetchDirectorInfo();
      fetchFairsInRegion();
    }
  }, [region, fetchDirectorInfo, fetchFairsInRegion]);

  //Get the status chip color based on the status of the region
  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Format date for display to users in a human-readable format
  const formatDateForDisplay = (dateTimeValue: any): string => {
    try {
      let dateTime: Date;

      // Handle Firestore Timestamp
      if (
        dateTimeValue &&
        typeof dateTimeValue === 'object' &&
        'toDate' in dateTimeValue
      ) {
        dateTime = dateTimeValue.toDate();
      } else if (dateTimeValue) {
        dateTime = new Date(dateTimeValue);
      } else {
        return 'Date not set';
      }

      if (isNaN(dateTime.getTime())) return 'Invalid date';

      // Format: April 21, 2025 at 2:30 PM
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      }).format(dateTime);
    } catch (error) {
      console.error('Error formatting date for display:', error);
      return 'Date formatting error';
    }
  };

  // Open registration dialog
  const handleOpenRegistration = (fair: Fair) => {
    setSelectedFair(fair);
    setDialogType('register');
    setOpenDialog(true);
    setRegistrationStatus('');
  };

  // Open unregistration dialog
  const handleOpenUnregistration = (fair: Fair) => {
    setSelectedFair(fair);
    setDialogType('unregister');
    setOpenDialog(true);
    setRegistrationStatus('');
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFair(null);
  };

  // Register as a judge
  const handleRegisterAsJudge = async () => {
    if (!selectedFair || !authStatus.user?.uid) {
      setRegistrationStatus(
        'Failed to register: User or fair information missing'
      );
      return;
    }

    // Check if user is already registered
    if (isUserRegistered(selectedFair)) {
      setRegistrationStatus(
        'You are already registered as a judge for this fair'
      );
      return;
    }

    try {
      // Add the user as a judge to the fair
      await addJudgeToFair(selectedFair.id, authStatus.user.uid);

      // Update the local state to reflect the change
      const updatedJudges = [
        ...(selectedFair.judges || []),
        { judgeId: authStatus.user.uid, judgeCheckedIn: false },
      ];

      setFairs(
        (currentFairs) =>
          currentFairs?.map((fair) =>
            fair.id === selectedFair.id
              ? { ...fair, judges: updatedJudges }
              : fair
          ) || null
      );

      setRegistrationStatus('Successfully registered as a judge');

      // Close the dialog after successful registration (after a short delay)
      setTimeout(() => {
        handleCloseDialog();
        // Refresh the fairs data to ensure everything is up-to-date
        fetchFairsInRegion();
      }, 1500);
    } catch (error) {
      console.error('Error registering as judge:', error);
      setRegistrationStatus('Failed to register. Please try again.');
    }
  };

  // Unregister as a judge
  const handleUnregisterAsJudge = async () => {
    if (!selectedFair || !authStatus.user?.uid) {
      setRegistrationStatus(
        'Failed to unregister: User or fair information missing'
      );
      return;
    }

    // Check if user is actually registered
    if (!isUserRegistered(selectedFair)) {
      setRegistrationStatus('You are not registered as a judge for this fair');
      return;
    }

    try {
      // Remove the user from the judges list
      await removeJudgeFromFair(selectedFair.id, authStatus.user.uid);

      // Update the local state
      const updatedJudges = selectedFair.judges.filter(
        (judge) => judge.judgeId !== authStatus.user?.uid
      );

      setFairs(
        (currentFairs) =>
          currentFairs?.map((fair) =>
            fair.id === selectedFair.id
              ? { ...fair, judges: updatedJudges }
              : fair
          ) || null
      );

      setRegistrationStatus('Successfully unregistered from the fair');

      // Close the dialog after successful unregistration (after a short delay)
      setTimeout(() => {
        handleCloseDialog();
        // Refresh the fairs data to ensure everything is up-to-date
        fetchFairsInRegion();
      }, 1500);
    } catch (error) {
      console.error('Error unregistering as judge:', error);
      setRegistrationStatus('Failed to unregister. Please try again.');
    }
  };

  // Check if user is already registered for a fair
  const isUserRegistered = (fair: Fair) => {
    return fair.judges.some(
      (judge) => judge.judgeId === (authStatus.user?.uid || '')
    );
  };

  // Count the number of judges for a fair
  const getJudgeCount = (fair: Fair) => {
    return fair.judges?.length || 0;
  };

  //Screen loading and error handling
  if (loading) {
    return <Typography>Loading region and fair information...</Typography>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!region) {
    return <Alert severity="info">No region and fair information found.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Region Details
      </Typography>

      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" gutterBottom>
                {region.name}
              </Typography>
              <Typography color="textSecondary" gutterBottom>
                Location: {region.location}, {region.state}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={region.status}
                  color={getStatusChipColor(region.status)}
                  sx={{ mr: 1 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="h6" gutterBottom>
                Region Director:
              </Typography>
              {directorInfo ? (
                <>
                  <Typography>
                    {directorInfo.firstName} {directorInfo.lastName}
                  </Typography>
                  <Typography color="textSecondary">
                    {directorInfo.email}
                  </Typography>
                </>
              ) : (
                <Typography color="textSecondary">
                  {region.regionDirectorId || 'No director assigned'}
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Fairs Section */}
      <Typography variant="h4" gutterBottom>
        Fairs in {region.name}
      </Typography>

      {fairs && fairs.length > 0 ? (
        <Grid container spacing={3}>
          {fairs.map((fair) => (
            <Grid item xs={12} sm={6} md={4} key={fair.id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {fair.name}
                  </Typography>
                  <Typography color="textSecondary">
                    {fair.addressLine}
                  </Typography>
                  <Typography color="textSecondary">
                    {fair.addressCity}, {fair.addressState} {fair.addressZip}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Date:</strong>{' '}
                      {formatDateForDisplay(fair.startDate)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Judges:</strong> {getJudgeCount(fair)}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {isUserRegistered(fair) ? (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleOpenUnregistration(fair)}
                          fullWidth
                        >
                          Unregister
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleOpenRegistration(fair)}
                          fullWidth
                        >
                          Register as Judge
                        </Button>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Alert severity="info">No fairs available in this region.</Alert>
      )}

      {/* Registration/Unregistration Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {dialogType === 'register'
            ? 'Register as Judge'
            : 'Unregister as Judge'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedFair && dialogType === 'register' && (
              <>
                Would you like to register as a judge for{' '}
                <strong>{selectedFair.name}</strong>
                <br />
                on {formatDateForDisplay(selectedFair.startDate)}?
              </>
            )}
            {selectedFair && dialogType === 'unregister' && (
              <>
                Are you sure you want to unregister as a judge for{' '}
                <strong>{selectedFair.name}</strong>
                <br />
                on {formatDateForDisplay(selectedFair.startDate)}?
              </>
            )}
          </DialogContentText>
          {registrationStatus && (
            <Alert
              severity={
                registrationStatus.includes('Failed') ? 'error' : 'success'
              }
              sx={{ mt: 2 }}
            >
              {registrationStatus}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          {dialogType === 'register' ? (
            <Button
              onClick={handleRegisterAsJudge}
              color="primary"
              variant="contained"
              disabled={registrationStatus.includes('Successfully')}
            >
              Register
            </Button>
          ) : (
            <Button
              onClick={handleUnregisterAsJudge}
              color="error"
              variant="contained"
              disabled={registrationStatus.includes('Successfully')}
            >
              Unregister
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FairRegistration;
